import { useEffect, useMemo, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import { StatCard, Card, StatusBadge, Spinner } from './ui';

/** Fetches all pages from an Amplify list query and returns the total count. */
async function countAll(
  listFn: (opts: { limit: number; nextToken?: string | null }) => Promise<{ data: any[]; nextToken?: string | null }>
): Promise<number> {
  let count = 0;
  let nextToken: string | null | undefined = undefined;
  do {
    const result = await listFn({ limit: 1000, nextToken });
    count += result.data.length;
    nextToken = result.nextToken;
  } while (nextToken);
  return count;
}

type Tab = 'overview' | 'clients' | 'audiences' | 'pixel' | 'contacts' | 'leads' | 'help';

export default function Overview({ onNavigate }: { onNavigate: (tab: Tab, params?: { source?: string }) => void }) {
  const client = useMemo(() => generateClient<Schema>(), []);
  const [stats, setStats] = useState({
    clients:    0,
    audiences:  0,
    contacts:   0,
    pixelHits:  0,
  });
  const [recentEvents,    setRecentEvents]    = useState<any[]>([]);
  const [recentAudiences, setRecentAudiences] = useState<any[]>([]);
  const [loading,         setLoading]         = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [cl, au, contactCount, pixelCount, ev] = await Promise.all([
          client.models.Client.list(),
          client.models.Audience.list(),
          countAll((opts) => client.models.Contact.list(opts)),
          countAll((opts) => client.models.VisitorEvent.list(opts)),
          client.models.VisitorEvent.list({ limit: 20 }),
        ]);

        setStats({
          clients:   cl.data.length,
          audiences: au.data.length,
          contacts:  contactCount,
          pixelHits: pixelCount,
        });

        const events = [...ev.data]
          .filter(e => e != null)
          .sort((a, b) => ((b.eventTimestamp ?? 0) - (a.eventTimestamp ?? 0)))
          .slice(0, 20);
        setRecentEvents(events);

        const auds = [...au.data]
          .sort((a, b) =>
            new Date(b.updatedAt ?? '').getTime() -
            new Date(a.updatedAt ?? '').getTime()
          )
          .slice(0, 5);
        setRecentAudiences(auds);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();

    // Real-time subscription for new pixel events
    const sub = client.models.VisitorEvent.onCreate().subscribe({
      next: (ev: any) => {
        setRecentEvents(prev => [ev, ...prev].slice(0, 20));
        setStats(s => ({ ...s, pixelHits: s.pixelHits + 1 }));
      },
      error: (e: any) => console.error('event sub error', e),
    });
    return () => sub.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Welcome */}
      <div>
        <h2 style={{
          fontSize: 22, fontWeight: 700, letterSpacing: '-0.03em',
          margin: '0 0 4px',
        }}>
          Good {getTimeOfDay()}, Hayden
        </h2>
        <p style={{ fontSize: 13, color: '#4a5d7a' }}>
          Here's what's happening across your audience platform.
        </p>
      </div>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
        <StatCard
          label="Active Clients"
          value={stats.clients}
          sub="Connected accounts"
          color="#3b82f6"
        />
        <StatCard
          label="Audiences"
          value={stats.audiences}
          sub="Pixel + Prospeo"
          color="#10b981"
        />
        <StatCard
          label="Contacts"
          value={stats.contacts.toLocaleString()}
          sub="Total across all clients"
          color="#8b5cf6"
        />
        <StatCard
          label="Pixel Events"
          value={stats.pixelHits.toLocaleString()}
          sub="Last 90 days"
          color="#f59e0b"
        />
      </div>

      {/* Two-column */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Live Event Feed */}
        <Card>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 16,
          }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, margin: 0, color: '#8a9bb5', display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{
                display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
                background: '#10b981', boxShadow: '0 0 6px #10b981',
                animation: 'pulse 2s infinite',
              }} />
              LIVE EVENTS
            </h3>
            <button
              onClick={() => onNavigate('contacts')}
              style={{
                background: 'none', border: 'none', color: '#3b82f6',
                cursor: 'pointer', fontSize: 12, fontFamily: 'Sora, sans-serif',
              }}
            >
              View all →
            </button>
          </div>

          {recentEvents.length === 0 ? (
            <p style={{ fontSize: 13, color: '#4a5d7a', textAlign: 'center', padding: '20px 0' }}>
              Waiting for events…
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
              {recentEvents.map(ev => (
                <div key={ev.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px',
                  background: '#080c14', borderRadius: 8,
                  border: '1px solid #111927',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 14 }}>{eventIcon(ev.eventName)}</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 500, color: '#e8edf5' }}>
                        {ev.eventName}
                      </div>
                      <div style={{
                        fontSize: 11, color: '#4a5d7a', fontFamily: 'IBM Plex Mono, monospace',
                        marginTop: 1, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {ev.url ? new URL(ev.url).pathname : '—'}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: '#4a5d7a', textAlign: 'right' }}>
                    {ev.eventTimestamp ? formatRelative(ev.eventTimestamp * 1000) : '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Recent Audiences */}
        <Card>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 16,
          }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, margin: 0, color: '#8a9bb5' }}>
              RECENT AUDIENCES
            </h3>
            <button
              onClick={() => onNavigate('audiences')}
              style={{
                background: 'none', border: 'none', color: '#3b82f6',
                cursor: 'pointer', fontSize: 12, fontFamily: 'Sora, sans-serif',
              }}
            >
              View all →
            </button>
          </div>

          {recentAudiences.length === 0 ? (
            <p style={{ fontSize: 13, color: '#4a5d7a', textAlign: 'center', padding: '20px 0' }}>
              No audiences built yet
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {recentAudiences.map(aud => (
                <div key={aud.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px',
                  background: '#080c14', borderRadius: 8,
                  border: '1px solid #111927',
                }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: '#e8edf5' }}>
                      {aud.audienceName}
                    </div>
                    <div style={{
                      fontSize: 11, color: '#4a5d7a', fontFamily: 'IBM Plex Mono, monospace',
                      marginTop: 2,
                    }}>
                      {(aud.memberCount ?? 0).toLocaleString()} members ·{' '}
                      <span style={{ color: aud.source === 'pixel' ? '#10b981' : '#3b82f6' }}>
                        {aud.source}
                      </span>
                    </div>
                  </div>
                  <StatusBadge status={aud.status ?? 'building'} />
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Quick actions */}
      <Card style={{ padding: '20px 24px' }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, color: '#8a9bb5', marginBottom: 16 }}>
          QUICK ACTIONS
        </h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { label: '+ Add Client',     tab: 'clients'   as Tab, color: '#3b82f6' },
            { label: '+ Build Audience', tab: 'audiences' as Tab, color: '#10b981' },
            { label: '↑ Import Contacts',tab: 'contacts'  as Tab, color: '#8b5cf6' },
            { label: '◉ Install Pixel',  tab: 'pixel'     as Tab, color: '#f59e0b' },
            { label: '📋 Form Leads',    tab: 'leads'     as Tab, color: '#a78bfa', source: 'website-form' },
          ].map(a => (
            <button
              key={a.tab + (a as any).source}
              onClick={() => onNavigate(a.tab, { source: (a as any).source })}
              style={{
                padding: '8px 16px',
                background: `${a.color}15`,
                border:     `1px solid ${a.color}30`,
                borderRadius: 7, color: a.color,
                cursor: 'pointer', fontSize: 13, fontWeight: 500,
                fontFamily: 'Sora, sans-serif',
                transition: 'all 0.15s',
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

function eventIcon(name: string) {
  if (!name) return '•';
  const n = name.toLowerCase();
  if (n.includes('form') || n.includes('lead')) return '📋';
  if (n.includes('page') || n.includes('view')) return '👁';
  if (n.includes('click')) return '🖱';
  if (n.includes('purchase') || n.includes('convert')) return '💰';
  return '⚡';
}

function formatRelative(ms: number) {
  const diff = Date.now() - ms;
  const s = Math.floor(diff / 1000);
  if (s < 60)  return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
